import { useState } from "react";
import { useForm } from "@mantine/form";
import {
  Alert,
  Anchor,
  Container,
  PasswordInput,
  Text,
  Paper,
  Group,
  Button,
  Stack,
} from "@mantine/core";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { useStore } from "../store";
import { userConfirmation } from "./authApi";
import { IconInfoCircle } from "@tabler/icons-react";
import LangSelector from "./LangSelector";

export default function Confirmation({ welcomeMessage, params, hideLangSelector }) {
  const { t } = useTranslation("Auth");
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null); // null | { linkInvalid: bool, message: string }
  const userLoggedIn = useStore((state) => state.userLoggedIn);
  const form = useForm({
    initialValues: {
      token: params.token,
      password: "",
    },
    validate: {
      password: (val) => (val.length < 8 ? t("invalid_password") : null),
    },
  });

  return (
    <Container size="xs" mt="xl">
      {error && (
        <Alert
          variant="light"
          color="red"
          title={t("activation_failed", "Account activation failed")}
          mb="lg"
          icon={<IconInfoCircle />}
        >
          <Stack gap="xs">
            <Text size="sm">{error.message}</Text>
            {error.linkInvalid && (
              <Group gap="md">
                <Anchor component={Link} href="/auth/reset" size="sm">
                  {t("request_new_link", "Send me a new link")}
                </Anchor>
                <Anchor component={Link} href="/auth/login" size="sm">
                  {t("login_instead")}
                </Anchor>
              </Group>
            )}
          </Stack>
        </Alert>
      )}

      <Paper radius="md" p="xl" withBorder>
        <Group align="flex-start" justify="space-between">
          <Text size="lg" fw={500}>
            {welcomeMessage || t("activation_title", "Set a password for your account")}
          </Text>
          {!hideLangSelector && <LangSelector />}
        </Group>
        <Text size="md" mt="lg" mb="lg">
          {t(
            "activation_message",
            "Choose a password to activate your account. You can use it to log in from now on.",
          )}
        </Text>

        <form
          onSubmit={form.onSubmit((values) => {
            setIsFetching(true);
            setError(null);

            userConfirmation(values)
              .then((data) => userLoggedIn(data))
              .catch((err) => {
                // 404 = token unknown or expired. The activation link is valid for seven days.
                const linkInvalid = err.status === 404 || err.status === 400;
                setError({
                  linkInvalid,
                  message: linkInvalid
                    ? t(
                        "activation_link_invalid",
                        "This activation link has expired or is not valid. Request a new link with your email address. If you already activated your account, log in instead.",
                      )
                    : err.status === 429
                      ? t(
                          "activation_rate_limited",
                          "Too many attempts. Please wait a minute and try again.",
                        )
                      : t(
                          "activation_error",
                          "We could not activate your account. Please try again in a moment.",
                        ),
                });
              })
              .finally(() => setIsFetching(false));
          })}
        >
          <Stack>
            <PasswordInput
              required
              disabled={isFetching}
              autoComplete="new-password"
              label={t("password")}
              placeholder={t("choose_password", "Choose a password (at least 8 characters)")}
              value={form.values.password}
              onChange={(event) =>
                form.setFieldValue("password", event.currentTarget.value)
              }
              error={form.errors.password}
              radius="md"
            />
          </Stack>

          <Group justify="flex-end" mt="xl">
            <Button type="submit" radius="xl" loading={isFetching}>
              {t("activate_account", "Activate account")}
            </Button>
          </Group>
        </form>
      </Paper>
    </Container>
  );
}
